import { useEffect, useMemo, useRef, useState } from "react";
import { ProjectBar } from "@/components/ProjectBar";
import { ToolContainer, downloadBlob } from "@/components/ToolLayout";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Download, Plus, Trash2, Upload } from "lucide-react";
import { toast } from "sonner";
import * as XLSX from "xlsx";
import { ToolInfoSection } from "@/components/ToolInfoSection";
import { useLogUsageOnAction } from "@/hooks/useLogUsageOnAction";
import { useProject } from "@/lib/workflow/useProject";
import { newId } from "@/lib/workflow/ids";
import { describeImport, exportRecords, importRecords, templateCsv } from "@/lib/workflow/layouts";
import type { Task } from "@/lib/workflow/types";
import { TEMPLATE_NAMES, addDaysIso, dueState, isoOf, migrateLegacyDone, parseIso, shiftDueDates, sortTasks, summarizeTasks, tasksFromTemplate, type DueState } from "@/lib/workflow/taskOps";

const TOOL_INFO_CONTENT = {
  what: "A checklist for planning an event. Start from a ready-made timeline (wedding, birthday, corporate event, conference, exhibition or fundraiser) counted back from your event date, then add your own tasks, give each an owner and a due date, and tick them off.",
  how: "Choose an event type and date and press 'Add these tasks'. Edit any task, add your own, filter by owner or by what is overdue, and move every date if the event date changes. Download Excel or CSV, copy the list as text, or import tasks from a CSV.",
  tip: "Lock in the tasks furthest out first (venue, key vendors, save-the-dates); they have the least room if you are late. Give every task an owner so nothing is 'someone's job'. Save the project file to keep the list.",
};

const LEGACY = ["fdp-event-date-v1", "fdp-event-time-v1", "fdp-event-checklist-done-v1"];
const today = () => isoOf(new Date());
const fmt = (iso: string) => { const d = parseIso(iso); return d ? d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" }) : iso; };
const STATE_NOTE: Record<DueState, string> = { done: "", overdue: "Overdue", today: "Due today", soon: "Due soon", later: "", undated: "" };
type Filter = "all" | "open" | "overdue";

export default function EventChecklistPlanner() {
  const [project, store] = useProject();
  const tasks = project.data.tasks;
  const { logUsageNow } = useLogUsageOnAction();
  const [type, setType] = useState(TEMPLATE_NAMES[0]);
  const [eventDate, setEventDate] = useState(() => addDaysIso(today(), 90) ?? today());
  const [filter, setFilter] = useState<Filter>("all");
  const [ownerFilter, setOwnerFilter] = useState("");
  const [newTitle, setNewTitle] = useState("");
  const [shiftBy, setShiftBy] = useState("7");
  const [report, setReport] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  // Bring over ticks saved by the earlier version of this tool, once.
  useEffect(() => {
    try {
      const raw = sessionStorage.getItem(LEGACY[2]);
      if (raw && store.get().data.tasks.length === 0) {
        const ticks = JSON.parse(raw) as unknown;
        if (Array.isArray(ticks) && ticks.length) {
          const first = String(ticks[0]);
          const legacyType = first.slice(0, first.indexOf(":"));
          const date = JSON.parse(sessionStorage.getItem(LEGACY[0]) ?? '""') as string;
          const migrated = migrateLegacyDone(ticks, tasksFromTemplate(legacyType === "Corporate Event" ? "Corporate event" : legacyType, parseIso(date) ? date : eventDate, () => newId("t")), legacyType === "Corporate Event" ? "Corporate Event" : legacyType);
          if (migrated.length) { store.setRecords("tasks", migrated); toast.success(`Brought over your earlier ${legacyType} checklist`); }
        }
      }
      LEGACY.forEach((k) => sessionStorage.removeItem(k));
    } catch { /* storage blocked or unreadable: start empty */ }
  }, [store]); // eslint-disable-line react-hooks/exhaustive-deps

  const now = today();
  const summary = useMemo(() => summarizeTasks(tasks, now), [tasks, now]);
  const owners = useMemo(() => Array.from(new Set(tasks.map((t) => t.owner).filter((o): o is string => !!o))).sort(), [tasks]);
  const visible = useMemo(() => sortTasks(tasks).filter((t) => {
    const st = dueState(t, now);
    if (filter === "open" && t.done) return false;
    if (filter === "overdue" && st !== "overdue") return false;
    return !ownerFilter || t.owner === ownerFilter;
  }), [tasks, filter, ownerFilter, now]);

  const update = (id: string, patch: Partial<Task>) => {
    const t = tasks.find((x) => x.id === id);
    if (!t) return;
    const next = { ...t, ...patch } as Task;
    for (const k of Object.keys(next) as (keyof Task)[]) if (k !== "title" && k !== "done" && (next[k] === undefined || next[k] === "")) delete next[k];
    store.upsertRecords("tasks", [next]);
  };
  const remove = (id: string) => store.setRecords("tasks", tasks.filter((t) => t.id !== id));
  const add = () => {
    const title = newTitle.trim();
    if (!title) return;
    store.upsertRecords("tasks", [{ id: newId("t"), title: title.slice(0, 300), done: false }]);
    setNewTitle("");
  };
  const applyTemplate = () => {
    if (!parseIso(eventDate)) { toast.error("Pick the event date first."); return; }
    const made = tasksFromTemplate(type, eventDate, () => newId("t"));
    const have = new Set(tasks.map((t) => t.title.toLowerCase()));
    const fresh = made.filter((t) => !have.has(t.title.toLowerCase()));
    if (!fresh.length) { toast.message("Those tasks are already on your list."); return; }
    store.setRecords("tasks", [...tasks, ...fresh]);
    toast.success(`Added ${fresh.length} task${fresh.length > 1 ? "s" : ""}${made.length > fresh.length ? ` (${made.length - fresh.length} already there)` : ""}`);
  };
  const shiftAll = () => {
    const n = Math.round(Number(shiftBy));
    if (!Number.isFinite(n) || n === 0) { toast.error("Enter the days to move, such as 7 or -14."); return; }
    store.setRecords("tasks", shiftDueDates(tasks, n));
    toast.success(`Moved every dated task by ${n} day${Math.abs(n) > 1 ? "s" : ""}.`);
  };

  const guard = async () => {
    if (!tasks.length) { toast.error("Add at least one task first."); return false; }
    return logUsageNow();
  };
  const copy = async () => {
    if (!(await guard())) return;
    const lines = sortTasks(tasks).map((t) => `[${t.done ? "x" : " "}] ${t.due ? fmt(t.due) + "  " : ""}${t.title}${t.owner ? ` (${t.owner})` : ""}`);
    await navigator.clipboard.writeText([project.name, "", ...lines].join("\n"));
    toast.success("Checklist copied to clipboard");
  };
  const exportExcel = async () => {
    if (!(await guard())) return;
    const head = ["Done", "Task", "Due", "Owner", "Category", "Notes"];
    const rows: (string | number)[][] = [[project.name], [`${summary.done} of ${summary.total} done`], [], head,
      ...sortTasks(tasks).map((t) => [t.done ? "yes" : "", t.title, t.due ?? "", t.owner ?? "", t.category ?? "", t.notes ?? ""])];
    const ws = XLSX.utils.aoa_to_sheet(rows);
    ws["!cols"] = [{ wch: 6 }, { wch: 44 }, { wch: 12 }, { wch: 18 }, { wch: 16 }, { wch: 30 }];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, "Checklist");
    downloadBlob(new Blob([XLSX.write(wb, { type: "array", bookType: "xlsx" })], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" }), "event-checklist.xlsx");
    toast.success("Checklist downloaded");
  };
  const exportCsv = async () => {
    if (!(await guard())) return;
    downloadBlob(new Blob([exportRecords("tasks", sortTasks(tasks))], { type: "text/csv;charset=utf-8" }), "event-checklist.csv");
    toast.success("Checklist downloaded");
  };
  const importFile = async (f: File | undefined) => {
    if (!f) return;
    const r = importRecords("tasks", await f.text(), { currency: project.currency });
    const errors = r.issues.filter((i) => i.level === "error");
    if (r.records.length) {
      const byId = new Map(tasks.map((t) => [t.id, t]));
      r.records.forEach((t) => byId.set(t.id, t));
      store.setRecords("tasks", Array.from(byId.values()));
      toast.success(describeImport(r, "tasks"));
    } else toast.error(errors[0]?.message ?? "Nothing could be imported");
    setReport(r.issues.length ? r.issues.slice(0, 8).map((i) => `Row ${i.row}: ${i.message}`).join("\n") : null);
  };

  const tiles = [
    { label: "Done", value: `${summary.percent}%`, color: "text-green-600" },
    { label: "Open", value: summary.open, color: "text-primary" },
    { label: "Overdue", value: summary.overdue, color: "text-red-600" },
    { label: "Due in 7 days", value: summary.dueSoon, color: "text-amber-600" },
  ];

  return (
    <ToolContainer>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-2" data-testid="chk-tiles">
        {tiles.map((t) => (
          <div key={t.label} className="rounded-lg border p-3 text-center"><p className={`text-2xl font-bold ${t.color}`}>{t.value}</p><p className="text-xs text-muted-foreground">{t.label}</p></div>
        ))}
      </div>
      <div className="h-2 rounded bg-muted overflow-hidden mb-5" role="progressbar" aria-valuenow={summary.percent} aria-valuemin={0} aria-valuemax={100} aria-label="Checklist progress">
        <div className="h-full bg-green-600 transition-all" style={{ width: `${summary.percent}%` }} />
      </div>

      <div className="rounded-xl border p-4 mb-5 space-y-3">
        <p className="text-sm font-medium">Start from a timeline</p>
        <div className="flex flex-wrap items-end gap-3">
          <div><Label className="text-xs">Event type</Label>
            <select className="border rounded px-2 py-2 bg-background text-sm block" aria-label="Event type" value={type} onChange={(e) => setType(e.target.value)} data-testid="chk-type">
              {TEMPLATE_NAMES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select></div>
          <div><Label className="text-xs">Event date</Label><Input type="date" value={eventDate} onChange={(e) => setEventDate(e.target.value)} aria-label="Event date" data-testid="chk-date" /></div>
          <Button onClick={applyTemplate} data-testid="chk-apply">Add these tasks</Button>
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <div><Label className="text-xs">Event date changed? Move every date by (days)</Label><Input className="w-24" inputMode="numeric" value={shiftBy} onChange={(e) => setShiftBy(e.target.value.replace(/[^\d-]/g, ""))} aria-label="Days to move" /></div>
          <Button variant="outline" onClick={shiftAll} disabled={!tasks.length} data-testid="chk-shift">Move dates</Button>
        </div>
      </div>

      <div className="flex gap-2 mb-3">
        <Input value={newTitle} maxLength={300} onChange={(e) => setNewTitle(e.target.value)} placeholder="Add your own task..." onKeyDown={(e) => e.key === "Enter" && add()} aria-label="New task" data-testid="chk-new" />
        <Button onClick={add} aria-label="Add task"><Plus className="h-4 w-4" /></Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-3">
        {(["all", "open", "overdue"] as Filter[]).map((f) => (
          <Button key={f} size="sm" variant={filter === f ? "default" : "outline"} onClick={() => setFilter(f)}>{f === "all" ? "All" : f === "open" ? "Not done" : "Overdue"}</Button>
        ))}
        {owners.length > 0 && (
          <select className="border rounded px-2 py-1 bg-background text-sm" aria-label="Owner" value={ownerFilter} onChange={(e) => setOwnerFilter(e.target.value)}>
            <option value="">Everyone</option>{owners.map((o) => <option key={o} value={o}>{o}</option>)}
          </select>
        )}
      </div>

      <div className="space-y-2" data-testid="chk-list">
        {tasks.length === 0 && <p className="text-sm text-muted-foreground">No tasks yet. Add a timeline above or type your own.</p>}
        {visible.map((t) => {
          const st = dueState(t, now);
          return (
            <div key={t.id} className={`flex flex-wrap items-center gap-2 rounded-lg border p-3 ${st === "done" ? "opacity-60 bg-muted/30" : st === "overdue" ? "border-red-300 bg-red-50 dark:bg-red-950/20" : "bg-card"}`}>
              <input type="checkbox" className="h-4 w-4" checked={t.done} onChange={() => update(t.id, { done: !t.done })} aria-label={`Mark "${t.title}" done`} />
              <Input className={`flex-1 min-w-[10rem] ${t.done ? "line-through" : ""}`} value={t.title} maxLength={300} aria-label="Task" onChange={(e) => update(t.id, { title: e.target.value })} />
              <Input className="w-40" type="date" value={t.due ?? ""} aria-label="Due date" onChange={(e) => update(t.id, { due: e.target.value || undefined })} />
              <Input className="w-32" value={t.owner ?? ""} placeholder="Owner" maxLength={120} aria-label="Owner" onChange={(e) => update(t.id, { owner: e.target.value })} />
              {STATE_NOTE[st] && <span className={`text-xs font-medium ${st === "overdue" ? "text-red-600" : "text-amber-600"}`}>{STATE_NOTE[st]}</span>}
              <Button size="icon" variant="ghost" aria-label="Remove task" onClick={() => remove(t.id)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          );
        })}
        {tasks.length > 0 && visible.length === 0 && <p className="text-sm text-muted-foreground">Nothing matches this filter.</p>}
      </div>

      <div className="flex flex-wrap gap-2 mt-4">
        <Button onClick={copy} disabled={!tasks.length}>Copy as text</Button>
        <Button variant="outline" onClick={exportExcel} disabled={!tasks.length} className="gap-2"><Download className="h-4 w-4" /> Download Excel</Button>
        <Button variant="outline" onClick={exportCsv} disabled={!tasks.length} className="gap-2"><Download className="h-4 w-4" /> Download CSV</Button>
        <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2"><Upload className="h-4 w-4" /> Import CSV</Button>
        <Button variant="ghost" size="sm" onClick={() => downloadBlob(new Blob([templateCsv("tasks")], { type: "text/csv;charset=utf-8" }), "checklist-template.csv")}>Blank CSV</Button>
        <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => { void importFile(e.target.files?.[0]); e.target.value = ""; }} />
      </div>
      {report && <pre className="mt-3 text-xs whitespace-pre-wrap rounded border p-3 bg-muted/30">{report}</pre>}
      <ProjectBar />
      <ToolInfoSection whatIsIt={TOOL_INFO_CONTENT.what} howToUse={TOOL_INFO_CONTENT.how} tip={TOOL_INFO_CONTENT.tip} />
    </ToolContainer>
  );
}
